import React, { useEffect, useRef, useState } from 'react';
import { Drawer, Form, Input, InputNumber, Button, message, Radio } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import TestRuleModal, { RuleConditions } from './TestRuleModal';
import {
  CREATE_ADDRESS_WHITELIST_RULE,
  UPDATE_ADDRESS_WHITELIST_RULE,
  ANALYZE_ADDRESS_SIMPLE,
  AnalyzeAddressSimpleResponse,
  AddressWhitelistRuleInput,
  AddressWhitelistRuleType,
  RULE_TYPE_BASIC,
  RULE_TYPE_REGEX,
  STATUS_ENABLED,
  ZIP_TYPE_EXACT,
  ZIP_TYPE_RANGE,
} from '../graphql/addressWhitelistRuleGraphql';

interface RuleFormDrawerProps {
  visible: boolean;
  record: AddressWhitelistRuleType | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface ValidationState {
  isValid: boolean;
  reason?: string;
}

type StandardizationMode = 'auto' | 'manual';

const RuleFormDrawer: React.FC<RuleFormDrawerProps> = ({ visible, record, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const isEdit = !!record;

  const [validation, setValidation] = useState<ValidationState | null>(null);
  const [mode, setMode] = useState<StandardizationMode>('auto');
  const [testModalVisible, setTestModalVisible] = useState(false);
  // 测试通过时记录当时的规则条件签名；当前条件签名与之一致才允许保存
  const [passedSignature, setPassedSignature] = useState<string | null>(null);

  const ruleType = Form.useWatch('ruleType', form);
  const zipType = Form.useWatch('zipType', form);
  const state = Form.useWatch('state', form);
  const city = Form.useWatch('city', form);
  const zipCode = Form.useWatch('zipCode', form);
  const zipStart = Form.useWatch('zipStart', form);
  const zipEnd = Form.useWatch('zipEnd', form);
  const streetInclude = Form.useWatch('streetInclude', form);
  const streetExclude = Form.useWatch('streetExclude', form);
  const regex = Form.useWatch('regex', form);

  const buildConditions = (): RuleConditions => {
    if (ruleType === RULE_TYPE_REGEX) {
      return {
        ruleType: RULE_TYPE_REGEX,
        state: null,
        city: null,
        zipType: null,
        zipCode: null,
        zipStart: null,
        zipEnd: null,
        streetInclude: null,
        streetExclude: null,
        regex: regex?.trim() || null,
      };
    }
    const isRange = zipType === ZIP_TYPE_RANGE;
    return {
      ruleType: RULE_TYPE_BASIC,
      state: state?.trim() || null,
      city: city?.trim() || null,
      zipType: zipType ?? ZIP_TYPE_EXACT,
      zipCode: isRange ? null : zipCode?.trim() || null,
      zipStart: isRange ? zipStart?.trim() || null : null,
      zipEnd: isRange ? zipEnd?.trim() || null : null,
      streetInclude: streetInclude?.trim() || null,
      streetExclude: streetExclude?.trim() || null,
      regex: null,
    };
  };

  const conditions = buildConditions();
  const conditionSignature = JSON.stringify(conditions);
  // 编辑模式规则条件锁定、无需重测，直接视为已通过；新建模式需通过规则测试
  const testPassed = isEdit || (passedSignature !== null && passedSignature === conditionSignature);

  // 防止编辑回填时把初始条件签名误判为「未测试」：回填后将初始签名视为已通过
  const initialSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visible) {
      return;
    }
    form.resetFields();
    setValidation(null);
    setTestModalVisible(false);
    if (record) {
      setMode('manual');
      form.setFieldsValue({
        ruleName: record.ruleName,
        priority: record.priority,
        status: record.status,
        ruleType: record.ruleType,
        addressId: record.addressId,
        standardizedFullAddress: record.fullStandardAddress,
        state: record.state ?? undefined,
        city: record.city ?? undefined,
        zipType: record.zipType ?? ZIP_TYPE_EXACT,
        zipCode: record.zipCode ?? undefined,
        zipStart: record.zipStart ?? undefined,
        zipEnd: record.zipEnd ?? undefined,
        streetInclude: record.streetInclude ?? undefined,
        streetExclude: record.streetExclude ?? undefined,
        regex: record.regex ?? undefined,
      });
      // 编辑场景：未改动条件时视为已通过，改动后签名变化会自动失效
      initialSignatureRef.current = null;
    } else {
      setMode('auto');
      form.setFieldsValue({
        priority: 100,
        status: STATUS_ENABLED,
        ruleType: RULE_TYPE_BASIC,
        zipType: ZIP_TYPE_EXACT,
      });
      initialSignatureRef.current = null;
      setPassedSignature(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, record]);

  // 编辑回填后，把回填出的初始条件签名标记为「已通过」（仅执行一次）
  useEffect(() => {
    if (visible && record && initialSignatureRef.current === null && ruleType != null) {
      initialSignatureRef.current = conditionSignature;
      setPassedSignature(conditionSignature);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, record, ruleType, conditionSignature]);

  // ===== 标准地址校验（自动模式）=====
  const saStreet = Form.useWatch('saStreetAddress', form);
  const saUnit = Form.useWatch('saUnit', form);
  const saCity = Form.useWatch('saCity', form);
  const saState = Form.useWatch('saState', form);
  const saZip = Form.useWatch('saZipcode', form);

  const [analyzeAddress, { loading: validating }] = useLazyQuery<AnalyzeAddressSimpleResponse>(
    ANALYZE_ADDRESS_SIMPLE,
    { fetchPolicy: 'network-only' }
  );

  const canValidate =
    !!saStreet?.trim() && !!saCity?.trim() && !!saState?.trim() && !!saZip?.trim();

  const handleModeChange = (e: RadioChangeEvent) => {
    setMode(e.target.value);
    form.setFieldValue('standardizedFullAddress', undefined);
    form.setFieldValue('addressId', undefined);
    setValidation(null);
  };

  const handleValidate = async () => {
    const { data, error } = await analyzeAddress({
      variables: {
        streetAddress: saStreet!.trim(),
        unit: saUnit?.trim() || null,
        city: saCity!.trim(),
        state: saState!.trim(),
        zipCode: saZip!.trim(),
        forceExternalApiMatch: true,
      },
    });
    if (error) {
      form.setFieldValue('standardizedFullAddress', undefined);
      form.setFieldValue('addressId', undefined);
      setValidation({ isValid: false, reason: error.message });
      return;
    }
    const result = data?.analyzeAddressSimple;
    if (!result) {
      form.setFieldValue('standardizedFullAddress', undefined);
      form.setFieldValue('addressId', undefined);
      setValidation({ isValid: false, reason: t('mapTool.addressWhitelistRule.drawer.validateNoResult') as string });
      return;
    }
    if (result.isValid && result.entityComponents) {
      const nc = result.entityComponents;
      const addressLine2 = nc.addressLine2 ? ` ${nc.addressLine2}` : '';
      const standardAddress = `${nc.addressLine1}${addressLine2}, ${nc.cityName}, ${nc.stateAbbreviation} ${nc.zipcode}`;
      form.setFieldValue('standardizedFullAddress', standardAddress);
      form.setFieldValue('addressId', nc.idLog ?? undefined);
      setValidation({ isValid: true });
    } else {
      form.setFieldValue('standardizedFullAddress', undefined);
      form.setFieldValue('addressId', undefined);
      setValidation({ isValid: false, reason: result.validationReason ?? undefined });
    }
  };

  // ===== 提交 =====
  const [createRule, { loading: creating }] = useMutation(CREATE_ADDRESS_WHITELIST_RULE);
  const [updateRule, { loading: updating }] = useMutation(UPDATE_ADDRESS_WHITELIST_RULE);
  const submitting = creating || updating;

  const validateBasicConditions = (): boolean => {
    const c = conditions;
    const hasAny =
      !!c.state || !!c.city || !!c.zipCode || !!c.zipStart || !!c.zipEnd ||
      !!c.streetInclude || !!c.streetExclude;
    if (!hasAny) {
      message.warning(t('mapTool.addressWhitelistRule.drawer.warnBasicAtLeastOne') as string);
      return false;
    }
    if (c.zipType === ZIP_TYPE_RANGE) {
      if (!c.zipStart || !c.zipEnd) {
        message.warning(t('mapTool.addressWhitelistRule.drawer.warnZipRangeRequired') as string);
        return false;
      }
      if (c.zipStart > c.zipEnd) {
        message.warning(t('mapTool.addressWhitelistRule.drawer.warnZipRangeOrder') as string);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    let values: Record<string, unknown>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (ruleType === RULE_TYPE_BASIC && !validateBasicConditions()) {
      return;
    }
    if (!testPassed) {
      message.warning(t('mapTool.addressWhitelistRule.drawer.warnTestFirst') as string);
      return;
    }
    const c = conditions;
    const input: AddressWhitelistRuleInput = {
      ruleName: (values.ruleName as string).trim(),
      priority: Number(values.priority),
      status: Number(values.status),
      addressId: Number(values.addressId),
      fullStandardAddress: (values.standardizedFullAddress as string).trim(),
      ruleType: c.ruleType,
      state: c.state,
      city: c.city,
      zipType: c.ruleType === RULE_TYPE_BASIC ? c.zipType : null,
      zipCode: c.zipCode,
      zipStart: c.zipStart,
      zipEnd: c.zipEnd,
      streetInclude: c.streetInclude,
      streetExclude: c.streetExclude,
      regex: c.regex,
    };
    try {
      if (isEdit && record) {
        await updateRule({ variables: { id: Number(record.id), input } });
        message.success(t('mapTool.addressWhitelistRule.drawer.messageUpdateSuccess') as string);
      } else {
        await createRule({ variables: { input } });
        message.success(t('mapTool.addressWhitelistRule.drawer.messageAddSuccess') as string);
      }
      onSuccess();
      onClose();
    } catch (err) {
      message.error((err as Error).message);
    }
  };

  const handleOpenTest = async () => {
    // 测试前确保已选择规则类型并填写了对应条件
    if (ruleType === RULE_TYPE_BASIC && !validateBasicConditions()) {
      return;
    }
    if (ruleType === RULE_TYPE_REGEX && !conditions.regex) {
      message.warning(t('mapTool.addressWhitelistRule.drawer.warnRegexRequired') as string);
      return;
    }
    setTestModalVisible(true);
  };

  return (
    <Drawer
      title={
        isEdit
          ? (t('mapTool.addressWhitelistRule.drawer.editTitle') as string)
          : (t('mapTool.addressWhitelistRule.drawer.title') as string)
      }
      width={600}
      open={visible}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('components.button.cancel')}</Button>
          <Button type="primary" loading={submitting} disabled={!testPassed} onClick={handleSubmit}>
            {t('components.button.save')}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="ruleName"
          label={t('mapTool.addressWhitelistRule.drawer.labelRuleName')}
          rules={[{ required: true, message: t('mapTool.addressWhitelistRule.drawer.ruleRuleNameRequired') as string }]}
        >
          <Input maxLength={128} disabled={isEdit} />
        </Form.Item>
        <Form.Item
          name="priority"
          label={t('mapTool.addressWhitelistRule.drawer.labelPriority')}
          rules={[{ required: true, message: t('mapTool.addressWhitelistRule.drawer.rulePriorityRequired') as string }]}
        >
          <InputNumber min={0} className="w-full" />
        </Form.Item>
        {/* 状态默认启用，不支持手工选择，仅作隐藏字段提交 */}
        <Form.Item name="status" hidden initialValue={STATUS_ENABLED}>
          <Input type="hidden" />
        </Form.Item>

        {/* ===== 标准地址 ===== */}
        {/* 编辑模式：仅直接编辑标准地址ID与标准地址，不再走自动校验流程 */}
        {!isEdit && (
          <Form.Item label={t('mapTool.addressWhitelistRule.drawer.labelStandardizationMode')}>
            <Radio.Group value={mode} onChange={handleModeChange}>
              <Radio value="auto">{t('mapTool.addressWhitelistRule.drawer.radioAutoMode')}</Radio>
              <Radio value="manual">{t('mapTool.addressWhitelistRule.drawer.radioManualMode')}</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        {!isEdit && mode === 'auto' && (
          <>
            <Form.Item name="saStreetAddress" label={t('mapTool.addressWhitelistRule.drawer.labelStreetAddress')}>
              <Input placeholder={t('mapTool.addressWhitelistRule.drawer.placeholderStreetAddress') as string} maxLength={255} />
            </Form.Item>
            <Form.Item name="saUnit" label={t('mapTool.addressWhitelistRule.drawer.labelUnit')}>
              <Input maxLength={255} />
            </Form.Item>
            <Form.Item name="saCity" label={t('mapTool.addressWhitelistRule.drawer.labelSaCity')}>
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="saState" label={t('mapTool.addressWhitelistRule.drawer.labelSaState')}>
              <Input maxLength={50} />
            </Form.Item>
            <Form.Item name="saZipcode" label={t('mapTool.addressWhitelistRule.drawer.labelSaZipcode')}>
              <Input maxLength={10} />
            </Form.Item>
            <div className="mb-4">
              <Button disabled={!canValidate} loading={validating} onClick={handleValidate}>
                {t('mapTool.addressWhitelistRule.drawer.buttonValidate')}
              </Button>
              {validation && !validation.isValid && (
                <div className="mt-2 px-3 py-2 rounded text-sm bg-red-50 text-red-600 border border-red-200">
                  {`${t('mapTool.addressWhitelistRule.drawer.validateFailed')}: ${validation.reason ?? ''}`}
                </div>
              )}
            </div>
          </>
        )}

        <Form.Item
          name="addressId"
          label={t('mapTool.addressWhitelistRule.drawer.labelAddressId')}
          rules={[{ required: true, message: t('mapTool.addressWhitelistRule.drawer.ruleAddressIdRequired') as string }]}
        >
          {mode === 'auto' ? <Input readOnly className="bg-gray-50 cursor-default" /> : <Input maxLength={50} />}
        </Form.Item>
        <Form.Item
          name="standardizedFullAddress"
          label={t('mapTool.addressWhitelistRule.drawer.labelStandardizedFullAddress')}
          rules={[{ required: true, message: t('mapTool.addressWhitelistRule.drawer.ruleStandardizedFullAddressRequired') as string }]}
        >
          {mode === 'auto' ? <Input readOnly className="bg-gray-50 cursor-default" /> : <Input maxLength={512} />}
        </Form.Item>

        {/* ===== 规则条件 ===== */}
        {/* 编辑模式：规则条件只读，不允许调整（仅可改优先级与标准地址） */}
        {isEdit && (
          <div className="mb-2 text-sm text-gray-400">
            {t('mapTool.addressWhitelistRule.drawer.ruleReadonlyHint')}
          </div>
        )}
        <Form.Item name="ruleType" label={t('mapTool.addressWhitelistRule.drawer.labelRuleType')}>
          <Radio.Group disabled={isEdit}>
            <Radio value={RULE_TYPE_BASIC}>{t('mapTool.addressWhitelistRule.ruleType.basic')}</Radio>
            <Radio value={RULE_TYPE_REGEX}>{t('mapTool.addressWhitelistRule.ruleType.regex')}</Radio>
          </Radio.Group>
        </Form.Item>

        {ruleType === RULE_TYPE_BASIC && (
          <>
            <Form.Item name="state" label={t('mapTool.addressWhitelistRule.drawer.labelState')}>
              <Input placeholder={t('mapTool.addressWhitelistRule.drawer.placeholderCommaSeparated') as string} maxLength={255} disabled={isEdit} />
            </Form.Item>
            <Form.Item name="city" label={t('mapTool.addressWhitelistRule.drawer.labelCity')}>
              <Input placeholder={t('mapTool.addressWhitelistRule.drawer.placeholderCommaSeparated') as string} maxLength={255} disabled={isEdit} />
            </Form.Item>
            <Form.Item name="zipType" label={t('mapTool.addressWhitelistRule.drawer.labelZipType')}>
              <Radio.Group disabled={isEdit}>
                <Radio value={ZIP_TYPE_EXACT}>{t('mapTool.addressWhitelistRule.zipType.exact')}</Radio>
                <Radio value={ZIP_TYPE_RANGE}>{t('mapTool.addressWhitelistRule.zipType.range')}</Radio>
              </Radio.Group>
            </Form.Item>
            {zipType === ZIP_TYPE_RANGE ? (
              <div className="grid grid-cols-2 gap-x-4">
                <Form.Item name="zipStart" label={t('mapTool.addressWhitelistRule.drawer.labelZipStart')}>
                  <Input maxLength={32} disabled={isEdit} />
                </Form.Item>
                <Form.Item name="zipEnd" label={t('mapTool.addressWhitelistRule.drawer.labelZipEnd')}>
                  <Input maxLength={32} disabled={isEdit} />
                </Form.Item>
              </div>
            ) : (
              <Form.Item name="zipCode" label={t('mapTool.addressWhitelistRule.drawer.labelZipCode')}>
                <Input placeholder={t('mapTool.addressWhitelistRule.drawer.placeholderCommaSeparated') as string} maxLength={255} disabled={isEdit} />
              </Form.Item>
            )}
            <Form.Item name="streetInclude" label={t('mapTool.addressWhitelistRule.drawer.labelStreetInclude')}>
              <Input maxLength={255} disabled={isEdit} placeholder={t('mapTool.addressWhitelistRule.drawer.placeholderCommaSeparated') as string}/>
            </Form.Item>
            <Form.Item name="streetExclude" label={t('mapTool.addressWhitelistRule.drawer.labelStreetExclude')}>
              <Input maxLength={255} disabled={isEdit} placeholder={t('mapTool.addressWhitelistRule.drawer.placeholderCommaSeparated') as string}/>
            </Form.Item>
          </>
        )}

        {ruleType === RULE_TYPE_REGEX && (
          <Form.Item name="regex" label={t('mapTool.addressWhitelistRule.drawer.labelRegex')}>
            <Input placeholder={t('mapTool.addressWhitelistRule.drawer.placeholderRegex') as string} maxLength={255} disabled={isEdit} />
          </Form.Item>
        )}

        {!isEdit && (
        <div className="mb-2">
          <Button onClick={handleOpenTest}>
            {t('mapTool.addressWhitelistRule.drawer.buttonTest')}
          </Button>
          <span className={`ml-3 text-sm ${testPassed ? 'text-green-600' : 'text-orange-500'}`}>
            {testPassed
              ? t('mapTool.addressWhitelistRule.drawer.testPassed')
              : t('mapTool.addressWhitelistRule.drawer.testRequired')}
          </span>
        </div>
        )}
      </Form>

      <TestRuleModal
        visible={testModalVisible}
        conditions={conditions}
        onClose={() => setTestModalVisible(false)}
        onPass={() => setPassedSignature(conditionSignature)}
      />
    </Drawer>
  );
};

export default RuleFormDrawer;
